import { Component, EventEmitter, OnDestroy, OnInit, Output } from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup, ValidatorFn, Validators } from "@angular/forms";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { Observable, Subscription } from "rxjs";
import { finalize, tap } from "rxjs/operators";
import { SmsSenderRequest, SmsSenderResponse, SenderType, DidByCompanyIdResponse, CallDirection } from "voxloud-types/dist/models";
import { SmsService } from "../../../../domains/sms/services/sms.service";
import { NotificationService } from "../../../../shared/services/notification/notification.service";
import { noWhitespaceValidator } from "../../../../shared/validators/input-field.validator";
import { DID } from 'voxloud-types/dist/models';
import { DidsService } from "../../../../domains/dids/services";

@Component({
    selector: 'gdp-add-edit-sender-popup',
    templateUrl: './add-edit-sender-popup.component.html',
    styleUrls: ['./add-edit-sender-popup.component.scss']
})
export class AddEditSenderPopupComponent implements OnInit, OnDestroy {
    public form: UntypedFormGroup;
    public subscriptions: Subscription[] = [];
    loading: boolean = false;
    SenderType = SenderType;
    retryCompanyDids: boolean = false;

    // inputs
    public title: string;
    public senderDetails: {
        label: string;
        placeholder: string;
        senderResponse?: SmsSenderResponse;
        senderType?: string;
        provider?: string;
        companyDids?: DID[];
    }
    public senderTypes = [
        {
            value: SenderType.ALPHANUMERIC,
            label: 'alphanumeric'
        },
        {
            value: SenderType.LONG_NUMBER,
            label: 'phone-number'
        }
    ];
    public selectedSenderType = this.senderTypes[0];
    public isEditMode: boolean;
    public btnText: string;
    public companyId: number;
    public senderId: number;
    public showSenderName: boolean = true;
    public selectedSenderDid: DID;

    @Output() onEmitAddSender: EventEmitter<string> = new EventEmitter();
    @Output() onEmitCompanyDids: EventEmitter<DID[]> = new EventEmitter();

    get senderNameControl() {
        return this.form.get('senderName');
    }

    constructor(
        private fb: UntypedFormBuilder,
        private smsService: SmsService,
        private activeModal: NgbActiveModal,
        private notification: NotificationService,
        private didsService: DidsService
    ) {
        this.form = this.fb.group({
            senderName: this.fb.control('', []),
            provider: this.fb.control('', []),
            senderType: this.fb.control(SenderType.ALPHANUMERIC, [Validators.required])
        });
    }

    ngOnInit(): void {
        if (this.isEditMode) {
            this.patchEditFlow();
        } else {
            this.onTypeSelect(this.selectedSenderType);
        }
    }

    private patchEditFlow(): void {
        this.selectedSenderType = this.senderTypes.filter(sender => sender.value === this.senderDetails?.senderResponse.type)?.[0] || this.senderTypes[0];
        this.onTypeSelect(this.selectedSenderType);
        if (this.selectedSenderType?.value === SenderType.ALPHANUMERIC) {
            this.form.get('senderName')?.setValue(this.senderDetails?.senderResponse.name);
        } else {
            let didFound = false;
            this.senderDetails.companyDids?.forEach(did => {
                if (did.number === '+' + this.senderDetails?.senderResponse.name) {
                    this.onProviderSelect(did);
                    didFound = true;
                }
            })
            if (!didFound) {
                if (this.senderDetails.companyDids && this.senderDetails.companyDids.length > 0) {
                    this.onProviderSelect(this.senderDetails.companyDids[0]);
                }
            }
        }
    }
    get senderNameError(): string | null {
        const control = this.senderNameControl;
        if (!control || !control.touched || control.valid) return null;
        if (control.hasError('required')) {
            return 'company.sms.error.required';
        }
        if (control.hasError('maxlength')) {
            return 'company.sms.error.maxlength';
        }
        if (control.hasError('whitespace')) {
            return 'company.sms.error.invalid';
        }
        return null;
    }

    public senderDetailsUpdate(): void {
        if (!this.isEditMode) {
            this.addSenderDetails();
        } else {
            this.editSenderDetails()
        }
    }

    public addSenderDetails(): void {
        const senderRequest: SmsSenderRequest = this.prepareSenderRequest();
        this.handleSenderRequest(
            this.smsService.addSendersDetails(this.companyId, senderRequest),
            'company.sms.success.add',
            senderRequest.name
        );
    }

    public editSenderDetails(): void {
        const senderRequest: SmsSenderRequest = this.prepareSenderRequest();
        this.handleSenderRequest(
            this.smsService.updateSendersDetails(this.companyId, this.senderId, senderRequest),
            'company.sms.success.edit',
            senderRequest.name
        );
    }

    private prepareSenderRequest(): SmsSenderRequest {
        const { senderType, senderName, provider } = this.form.controls;
        const type = senderType.value;
        const isAlphanumeric = type === SenderType.ALPHANUMERIC;
        const isLongNumber = type === SenderType.LONG_NUMBER;
        const direction = [CallDirection.OUTBOUND];
        const did = this.senderDetails?.companyDids?.find(
            d => d.number === this.selectedSenderDid?.number
        );
        if (isLongNumber && did?.is_inbound_sms_enabled) {
            direction.push(CallDirection.INBOUND);
        }
        return {
            name: isAlphanumeric ? senderName.value : this.selectedSenderDid?.number,
            type: type,
            sender_provider: isLongNumber ? provider.value : undefined,
            direction
        };
    }

    public handleSenderRequest(request$: Observable<SmsSenderResponse>, successMessage: string, senderName: string): void {
        this.loading = true;
        const sub = request$.pipe(
            tap(() => {
                let newSender = senderName;
                if (this.selectedSenderType.value !== SenderType.ALPHANUMERIC) {
                    // Remove '+' only if it appears at the start
                    if (senderName.startsWith('+')) {
                        newSender = senderName.substring(1);
                    }
                }
                this.onEmitAddSender.emit(newSender);
            }),
            finalize(() => {
                this.loading = false;
                this.close();
            })
        ).subscribe(() => {
            this.notification.success(successMessage);
        });
        this.subscriptions.push(sub);
    }

    public onTypeSelect(senderType: { value: SenderType; label: string }): void {
        this.selectedSenderType = senderType;
        this.form.get('senderType')?.setValue(senderType?.value);
        const isAlphanumeric = this.selectedSenderType?.value === SenderType.ALPHANUMERIC;
        this.updateFieldValidation('senderName', isAlphanumeric, [Validators.required, Validators.maxLength(11), noWhitespaceValidator]);
        this.updateFieldValidation('provider', !isAlphanumeric, [Validators.required]);
        if (isAlphanumeric) {
            this.showSenderName = true;
        } else {
            this.form.get('senderName')?.setValue(null);
            this.showSenderName = false;
            if (this.senderDetails.companyDids?.length > 0) {
                this.onProviderSelect(this.senderDetails.companyDids[0]);
            }
        }
    }

    public onProviderSelect(did: DID): void {
        this.selectedSenderDid = did;
        this.form.get('provider')?.setValue(this.selectedSenderDid?.provider);
    }

    /**
     * Update field validation - make it required or optional
     * @param fieldName - Name of the form control
     * @param isRequired - true = required, false = optional
     */
    public updateFieldValidation(fieldName: string, isRequired: boolean, validators: ValidatorFn[]): void {
        const control = this.form.get(fieldName);
        if (isRequired) {
            control.setValidators(validators);
        } else {
            control.clearValidators();
        }
        control.updateValueAndValidity();
    }

    public isFormUnchanged(): boolean {
        if (this.isEditMode) {
            const currentType = this.form.get('senderType')?.value;
            const currentName = this.form.get('senderName')?.value;
            const { type, name } = this.senderDetails?.senderResponse;
            if (currentType === type) {
                if (currentType === SenderType.ALPHANUMERIC) {
                    return currentName === name;
                } else if (currentType === SenderType.LONG_NUMBER) {
                    return this.selectedSenderDid?.number === '+' + name;
                } else {
                    return true;
                }
            }
            return false;
        }
        return false;
    }

    public close(): void {
        this.activeModal.close();
    }

    public getDids(): void {
        this.retryCompanyDids = true;
        this.subscriptions.push(this.didsService.getDidsByCompanyId(this.companyId)
            .subscribe(
                (response: DidByCompanyIdResponse[]) => {
                    if (response && response.length > 0) {
                        this.senderDetails.companyDids = response[0].dids.filter(did => !did.is_from_pool);
                        this.retryCompanyDids = false;
                        this.onEmitCompanyDids.emit(this.senderDetails.companyDids);
                        if (this.isEditMode) {
                            this.patchEditFlow();
                        } else {
                            this.onTypeSelect(this.selectedSenderType);
                        }
                    }
                }, () => {
                    this.retryCompanyDids = false;
                }
            ));
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(s => s?.unsubscribe());
    }
}